/**
 * H3 Hexagon Aggregation Layer
 *
 * Aggregates point data (POIs, cities, etc.) into H3 hexagonal bins
 * for density visualization. Uses Deck.gl H3HexagonLayer for rendering.
 *
 * H3 resolution guide (approximate hex edge length):
 *   3 → ~12 km (country-level overview)
 *   4 → ~6 km  (region-level)
 *   5 → ~2 km  (city-level)
 *   6 → ~700 m (neighborhood-level)
 *   7 → ~260 m (street-level)
 */
import { latLngToCell, cellToBoundary } from 'h3-js'
import { H3HexagonLayer } from '@deck.gl/geo-layers'
import { scaleSequential } from 'd3-scale'
import { interpolateYlOrRd, interpolateViridis, interpolatePlasma } from 'd3-scale-chromatic'

const COLOR_RAMPS = {
  YlOrRd: interpolateYlOrRd,
  viridis: interpolateViridis,
  plasma: interpolatePlasma,
}

/**
 * Choose H3 resolution based on map zoom level.
 * Higher zoom → finer resolution hexagons.
 */
function zoomToResolution(zoom) {
  if (zoom < 3) return 2
  if (zoom < 5) return 3
  if (zoom < 7) return 4
  if (zoom < 9) return 5
  if (zoom < 11) return 6
  return 7
}

/**
 * Choose H3 resolution based on point count (for when zoom is unreliable,
 * e.g., during a fly animation). More points → finer resolution to avoid
 * massive blobs that obscure the map.
 *
 * @param {number} pointCount — number of POI points
 * @param {number} [zoom] — optional current zoom (used as secondary signal)
 * @returns {number} H3 resolution (2-7)
 */
export function smartResolution(pointCount, zoom) {
  // If zoom is high enough to be meaningful, use zoom-based resolution
  if (zoom != null && zoom >= 7) return zoomToResolution(zoom)

  // Otherwise, pick based on point density:
  // Few points → coarser hex (so hexagons are visible)
  // Many points → finer hex (so density gradient emerges)
  if (pointCount < 50) return 3
  if (pointCount < 500) return 4
  if (pointCount < 5000) return 4
  if (pointCount < 20000) return 5
  return 5 // cap at 5 for country-level views (~2km hexes)
}

/**
 * Aggregate an array of points into H3 hexagonal bins.
 * @param {Array} points - Array of { lng, lat, ...props }
 * @param {number} resolution - H3 resolution (0-15)
 * @returns {Array} Array of { hexId, count, points }
 */
export function aggregateToH3(points, resolution) {
  const bins = new Map()

  for (const point of points) {
    const hexId = latLngToCell(point.lat, point.lng, resolution)
    if (!bins.has(hexId)) {
      bins.set(hexId, { hexId, count: 0, points: [] })
    }
    const bin = bins.get(hexId)
    bin.count++
    // Only keep first 5 points per bin for popup display
    if (bin.points.length < 5) {
      bin.points.push(point)
    }
  }

  return Array.from(bins.values())
}

/**
 * Create a Deck.gl H3HexagonLayer from aggregated hex data.
 * @param {string} id - Layer ID
 * @param {Array} hexData - Array from aggregateToH3()
 * @param {object} options - { colorRamp, opacity, elevationScale }
 * @returns {H3HexagonLayer}
 */
export function createH3Layer(id, hexData, options = {}) {
  const { colorRamp = 'YlOrRd', opacity = 0.55, elevationScale = 0, extruded = false } = options

  if (!hexData || hexData.length === 0) {
    return new H3HexagonLayer({ id, data: [] })
  }

  const maxCount = Math.max(...hexData.map((d) => d.count))
  const rampFn = COLOR_RAMPS[colorRamp] || interpolateYlOrRd
  // Domain [0, maxCount] — pass raw d.count, NOT pre-normalized t
  const colorScale = scaleSequential(rampFn).domain([0, maxCount])

  // For extruded mode: filter out the bottom 10% of hexagons (low-count noise)
  // and scale elevation logarithmically so dense areas really tower
  const sortedCounts = hexData.map((d) => d.count).sort((a, b) => a - b)
  const p10 = sortedCounts[Math.floor(sortedCounts.length * 0.1)] || 1
  const displayData = extruded ? hexData.filter((d) => d.count >= p10) : hexData

  return new H3HexagonLayer({
    id,
    data: displayData,
    pickable: true,
    filled: true,
    stroked: !extruded,
    lineWidthMinPixels: extruded ? 0 : 1,
    extruded,
    opacity: extruded ? 0.8 : opacity,
    getHexagon: (d) => d.hexId,
    getFillColor: (d) => {
      const css = colorScale(d.count)
      const t = d.count / maxCount
      if (extruded) {
        // Bolder colors for 3D — fully opaque
        return cssToRgba(css, 255)
      }
      // Flat mode: low-density hexagons are more transparent
      return cssToRgba(css, Math.round(60 + 195 * t))
    },
    getLineColor: extruded ? [0, 0, 0, 0] : [255, 210, 0, 60],
    getElevation: extruded ? (d) => Math.log2(1 + d.count) * elevationScale : () => 0,
    elevationScale: extruded ? 1 : 0,
    updateTriggers: {
      getFillColor: [maxCount, colorRamp, extruded],
      getElevation: [elevationScale, extruded],
    },
  })
}

/**
 * Convert pre-aggregated hex data into a GeoJSON FeatureCollection carrying a
 * per-feature fill colour, for rendering with MapLibre's own renderer.
 *
 * Why not Deck.gl: deck positions its geometry with its own Web-Mercator
 * viewport, which does not follow MapLibre v5's globe projection. The hexagons
 * therefore sit on a flat plane while the basemap is a sphere, and visibly
 * peel away from the globe as soon as the camera is tilted. MapLibre's own
 * fill layers are projection-aware, so they stay glued to the surface at any
 * pitch and drape over terrain correctly.
 *
 * Colours mirror createH3Layer exactly (sequential ramp over [0, maxCount],
 * with sparse cells more transparent) so the two paths look identical.
 *
 * @param {Array} hexData - [{ hexId, count, rawCount?, population?, perCapita? }]
 * @param {string} colorRamp
 * @returns {object} GeoJSON FeatureCollection
 */
export function buildH3GeoJSON(hexData, colorRamp = 'YlOrRd') {
  if (!hexData || hexData.length === 0) {
    return { type: 'FeatureCollection', features: [] }
  }

  const maxCount = Math.max(...hexData.map((d) => d.count))
  const rampFn = COLOR_RAMPS[colorRamp] || interpolateYlOrRd
  const colorScale = scaleSequential(rampFn).domain([0, maxCount || 1])

  const features = []
  for (const d of hexData) {
    let ring
    try {
      // formatAsGeoJson = true → [lng, lat] pairs
      ring = cellToBoundary(d.hexId, true)
    } catch {
      continue // unreadable cell id — skip rather than fail the whole layer
    }
    if (!ring || ring.length < 3) continue

    // A hexagon straddling the antimeridian would otherwise smear right
    // across the map as a horizontal band.
    let minLng = Infinity
    let maxLng = -Infinity
    for (const pt of ring) {
      if (pt[0] < minLng) minLng = pt[0]
      if (pt[0] > maxLng) maxLng = pt[0]
    }
    if (maxLng - minLng > 180) continue

    // GeoJSON rings must close; h3-js does not close them.
    const coords = ring.slice()
    const first = coords[0]
    const last = coords[coords.length - 1]
    if (first[0] !== last[0] || first[1] !== last[1]) coords.push(first)

    const t = maxCount > 0 ? d.count / maxCount : 0
    features.push({
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [coords] },
      properties: {
        hexId: d.hexId,
        count: d.count,
        rawCount: d.rawCount != null ? d.rawCount : d.count,
        population: d.population || 0,
        perCapita: d.perCapita != null ? d.perCapita : null,
        _fill: colorScale(d.count),
        _alpha: Math.round((60 + 195 * t) / 2.55) / 100,
        // Extrusion height in metres, log-scaled so dense cells tower without
        // the tallest column dwarfing everything else. Matches the elevation
        // curve Deck.gl used (log2(1 + count) * 5000).
        _height: Math.log2(1 + d.count) * 5000,
      },
    })
  }

  return { type: 'FeatureCollection', features }
}

/**
 * Build a legend config for the H3 layer.
 */
export function buildH3LegendConfig(hexData, label, colorRamp = 'YlOrRd') {
  if (!hexData || hexData.length === 0) return { visible: false }

  const maxCount = Math.max(...hexData.map((d) => d.count))
  const rampFn = COLOR_RAMPS[colorRamp] || interpolateYlOrRd

  return {
    visible: true,
    title: label || 'Density',
    type: 'gradient',
    colorRamp: rampFn,
    min: 0,
    max: maxCount,
    format: (v) => v.toLocaleString(),
  }
}

/** Convert CSS color string (rgb/hex) to [r, g, b, a] array */
function cssToRgba(css, alpha = 200) {
  const m = css.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/)
  if (m) return [+m[1], +m[2], +m[3], alpha]
  // Fallback for hex
  if (css.startsWith('#')) {
    const hex = css.slice(1)
    return [
      parseInt(hex.slice(0, 2), 16),
      parseInt(hex.slice(2, 4), 16),
      parseInt(hex.slice(4, 6), 16),
      alpha,
    ]
  }
  return [255, 140, 0, alpha]
}

export { zoomToResolution }
